import React, { useState, useEffect, useMemo } from "react";
import { Card } from "../ui/Card";
import { Heading } from "../ui/Heading";
import { Button } from "../ui/Button";
import { getThumbnailUrl, getImageUrl } from "../../utils/imageUtils";
import { customizationAssetApi, CustomizationAsset } from "../../services/customizationAssetApi";

export interface BackgroundLibraryProps {
  onSelectBackground?: (backgroundUrl: string) => void;
  selectedBackground?: string;
  backgroundImages?: string[]; // Selected background images from product assets library
}

export const BackgroundLibrary: React.FC<BackgroundLibraryProps> = ({
  onSelectBackground,
  selectedBackground,
  backgroundImages = [],
}) => {
  const [selectedId, setSelectedId] = useState<string | undefined>(selectedBackground);
  const [assets, setAssets] = useState<CustomizationAsset[]>([]);

  // Fetch asset details to get originalUrl for thumbnails
  useEffect(() => {
    const fetchAssets = async () => {
      try {
        const allAssets = await customizationAssetApi.getAssets("background");
        setAssets(allAssets);
      } catch (error) {
        console.warn("Failed to fetch assets for thumbnails:", error);
      }
    };

    if (backgroundImages.length > 0) {
      fetchAssets();
    }
  }, [backgroundImages.length]);

  // Helper to extract filename from URL for matching
  const getFilename = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return urlObj.pathname.split("/").pop() || "";
    } catch {
      return url.split("/").pop() || "";
    }
  };

  // Map background images with thumbnail URLs from original (non-watermarked) versions
  const backgrounds = useMemo(() => {
    if (assets.length === 0) {
      // If assets haven't loaded yet, return backgrounds with fallback thumbnail URLs
      return backgroundImages.map((url, index) => ({
        id: `bg-${index}`,
        name: `Background ${index + 1}`,
        url,
        thumbnailUrl: getThumbnailUrl(url),
      }));
    }

    return backgroundImages.map((url, index) => {
      // Find matching asset by comparing URLs/filenames
      // For admins, url might be the original, for users it's watermarked
      const matchingAsset = assets.find((asset) => {
        const assetFilename = getFilename(asset.url);
        const originalFilename = asset.originalUrl ? getFilename(asset.originalUrl) : "";
        const urlFilename = getFilename(url);
        
        // Match by exact URL or filename
        return asset.url === url || 
               asset.originalUrl === url ||
               assetFilename === urlFilename || 
               originalFilename === urlFilename;
      });

      // For thumbnails, prefer the asset's thumbnailUrl field if available
      // Otherwise, generate from the original (non-watermarked) version
      let thumbnailUrl: string;
      if (matchingAsset?.thumbnailUrl) {
        // Use the thumbnail URL from the asset (this is the stored thumbnail)
        // thumbnailUrl is already a full URL (cloud) or path (local), just convert to full URL if needed
        thumbnailUrl = getImageUrl(matchingAsset.thumbnailUrl);
      } else if (matchingAsset) {
        // If no thumbnailUrl in asset, generate from original (non-watermarked) version
        const originalUrlForThumbnail = matchingAsset.originalUrl || matchingAsset.url;
        thumbnailUrl = getThumbnailUrl(originalUrlForThumbnail);
      } else {
        // If no matching asset found, generate from the provided URL
        thumbnailUrl = getThumbnailUrl(url);
      }

      return {
        id: `bg-${index}`,
        name: matchingAsset?.name || `Background ${index + 1}`,
        url, // Keep the original url for selection (watermarked for users, original for admins)
        thumbnailUrl, // Thumbnail from original (non-watermarked) version
      };
    });
  }, [backgroundImages, assets]);

  const handleSelect = (backgroundUrl: string) => {
    setSelectedId(backgroundUrl);
    if (onSelectBackground) {
      onSelectBackground(backgroundUrl);
    }
  };

  return (
    <Card padding="md">
      <Heading size="sm" className="mb-4">Background Library</Heading>
      {backgrounds.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-8">
          No backgrounds available. Please select backgrounds from the assets library in the product settings.
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 max-h-96 overflow-y-auto">
          {backgrounds.map((background) => (
            <button
              key={background.id}
              type="button"
              onClick={() => handleSelect(background.url)}
              className={`
                relative aspect-square border-2 rounded overflow-hidden
                ${selectedId === background.url ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-300"}
                hover:border-blue-400 transition-colors bg-gray-100
              `}
            >
              <img
                src={background.thumbnailUrl}
                alt={background.name}
                className="w-full h-full object-cover"
                style={{
                  userSelect: "none",
                  pointerEvents: "none",
                  WebkitUserDrag: "none",
                } as React.CSSProperties}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
                onError={(e) => {
                  // Fallback to gray square if image fails to load
                  const target = e.target as HTMLImageElement;
                  target.style.display = "none";
                  const parent = target.parentElement;
                  if (parent) {
                    const fallback = document.createElement("div");
                    fallback.className = "w-full h-full bg-gray-200 flex items-center justify-center";
                    fallback.innerHTML = `<div class="text-xs text-gray-500 p-2 text-center">${background.name}</div>`;
                    parent.appendChild(fallback);
                  }
                }}
              />
              {selectedId === background.url && (
                <div className="absolute top-1 right-1 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs z-10">
                  ✓
                </div>
              )}
            </button>
          ))}
        </div>
      )}
      <div className="mt-4">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelectedId(undefined);
            if (onSelectBackground) {
              onSelectBackground("");
            }
          }}
          className="w-full"
        >
          Clear Background
        </Button>
      </div>
    </Card>
  );
};

export default BackgroundLibrary;

